import React from 'react';
import i18n from '../../../i18n';
import { fmt } from '../data/metrics';
import { SOURCE_ORDER, sourceMeta } from '../data/reservationSources';
import type { OpsCommandLivePayload } from '../types';
import type { ComparisonItem } from './Topbar';
import { Icon } from './shared/Icon';
import { PerformanceLoader } from './PerformanceLoader';

type ComparisonLiveItem = ComparisonItem & {
  live: OpsCommandLivePayload | null;
};

// Comparison rendering deliberately reuses the original Live-tab cards
// (ribbon-card, kcl clusters, ol-card) with one compact row per compared
// scope inside. The scope color appears only as a small identifying dot -
// values keep the dashboard's normal ink.
export function ComparisonLiveView({
  items,
  loading,
}: {
  items: ComparisonLiveItem[];
  loading: boolean;
}) {
  const ready = items.filter(i => i.live?.kpis != null);
  if (loading || ready.length === 0) {
    return <PerformanceLoader label="Comparing tonight's service" />;
  }

  const anyRecovery = ready.some(i => i.live?.recovery);

  return (
    <div data-ftue="comparison-view">
      <section className="ribbon">
        <div className="sec-head">
          <h2>Service timeline</h2>
          <span className="sh-meta">
            Tonight side by side · {ready.length} scopes
          </span>
        </div>
        <div className="ribbon-card" style={{ paddingBottom: 4 }}>
          {ready.map(item => (
            <ServiceCompareRow key={item.filter} item={item} />
          ))}
        </div>
      </section>

      {/* Act 1b - the original cluster cards, rows per scope */}
      <div className="sec-head">
        <h2>Group overview</h2>
        <span className="sh-meta">
          Real-time · compared across {ready.length} scopes
        </span>
        <span
          className="prov-note"
          title="Built from reservations. POS, review, weather and event signals are not connected."
        >
          From reservations
        </span>
      </div>
      <div className="kpi-clusters">
        <CompareCluster
          icon="flag"
          label="Flake risk"
          sub="no-show / cancel"
          items={ready}
          render={k => (
            <>
              <span>{k.live!.kpis!.noshow}%</span>
              <span className="cmpx-sub">/</span>
              <span>{k.live!.kpis!.cancel}%</span>
            </>
          )}
        />
        <CompareCluster
          icon="refresh"
          label="Seating efficiency"
          sub="PAX/table · dwell · turns"
          items={ready}
          render={k => (
            <>
              <span>{k.live!.kpis!.efficiency}</span>
              <span className="cmpx-sub">PAX</span>
              <span>{k.live!.kpis!.seatTime}m</span>
              <span>{k.live!.kpis!.turns}×</span>
            </>
          )}
        />
        <CompareCluster
          icon="euro"
          label="Revenue"
          sub="banked today"
          items={ready}
          render={k => (
            <>
              <span>{fmt.money(k.live!.kpis!.revenue)}</span>
              <span className="cmpx-sub">€{k.live!.kpis!.spend}/guest</span>
            </>
          )}
        />
        <CompareCluster
          icon="euro"
          label="Sell items"
          sub="pre-ordered via widget"
          items={ready}
          render={k => (
            <>
              <span>{fmt.eur(k.live!.kpis!.sellRevenue)}</span>
              <span className="cmpx-sub">{k.live!.kpis!.sellAttach}% attach</span>
            </>
          )}
        />
      </div>

      {/* Act 2 - source mix, one familiar stacked bar per scope */}
      <div className="act-head">Where demand comes from</div>
      <CompareSourceMix items={ready} />

      {/* Act 3 - recovery, one row per scope */}
      {anyRecovery && (
        <>
          <div className="act-head">Act on it</div>
          <div className="sec-head">
            <h2>Recovery</h2>
            <span className="sh-meta">
              No-show &amp; cancellation recovery tonight
            </span>
          </div>
          <div className="ol-card" style={{ marginBottom: 18 }}>
            <div className="cmpx-rows">
              {ready.map(item => {
                const r = item.live?.recovery;
                return (
                  <div key={item.filter} className="cmpx-row">
                    <span
                      className="cmp-chip-dot"
                      style={{ background: item.color }}
                    />
                    <span className="cmpx-name">{item.label}</span>
                    <span className="cmpx-vals">
                      {r ? (
                        <>
                          <span>{r.recoveredCov}</span>
                          <span className="cmpx-sub">recovered</span>
                          <span>{r.recoverableCov}</span>
                          <span className="cmpx-sub">recoverable</span>
                          <span>{r.lostCov}</span>
                          <span className="cmpx-sub">lost</span>
                          {r.captureRate != null && (
                            <>
                              <span>{Math.round(r.captureRate * 100)}%</span>
                              <span className="cmpx-sub">capture</span>
                            </>
                          )}
                        </>
                      ) : (
                        <span className="cmpx-sub">no recovery data</span>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* Act 4 - combined fleet table, scope shown as a dot */}
      <div className="act-head">The fleet</div>
      <CompareFleetTable items={ready} />
    </div>
  );
}

function ServiceCompareRow({ item }: { item: ComparisonLiveItem }) {
  const k = item.live!.kpis!;
  const fc = item.live!.forecast;
  const bookedByClose = Math.max(
    fc?.projectedCovers ?? 0,
    k.covers + k.incoming,
  );
  const stillBooked = Math.max(0, bookedByClose - k.covers - k.incoming);

  return (
    <div className="cmpx-service-row">
      <div className="cmpx-scope">
        <span className="cmp-chip-dot" style={{ background: item.color }} />
        <b>{item.label}</b>
        <span>{k.outlets} outlets in service</span>
      </div>
      <div className="cmpx-service-stats">
        <div className="cmpx-stat">
          <span className="cmpx-stat-v">{fmt.num(k.covers)}</span>
          <span className="cmpx-stat-l">guests served</span>
        </div>
        <div className="cmpx-stat">
          <span className="cmpx-stat-v">
            {k.occupancy}
            <span className="rib-u">%</span>
          </span>
          <span className="cmpx-stat-l">occupancy now</span>
        </div>
        <div className="cmpx-stat">
          <span className="cmpx-stat-v" style={{ color: 'var(--accent)' }}>
            +{k.incoming}
          </span>
          <span className="cmpx-stat-l">booked · next 2h</span>
        </div>
        <div className="cmpx-stat">
          <span className="cmpx-stat-v">{fmt.num(bookedByClose)}</span>
          <span className="cmpx-stat-l">booked by close</span>
        </div>
        <div className="cmpx-stat">
          <span className="cmpx-stat-v">
            {k.seated}
            <span className="rib-u"> of {fmt.num(k.seats)}</span>
          </span>
          <span className="cmpx-stat-l">seated now</span>
        </div>
      </div>
      <div
        className="segbar"
        role="img"
        aria-label="served, booked and projected guests"
      >
        <span
          className="seg served"
          style={{
            width: `${(k.covers / Math.max(bookedByClose, 1)) * 100}%`,
          }}
        />
        {k.incoming > 0 && (
          <span
            className="seg booked"
            style={{
              width: `${(k.incoming / Math.max(bookedByClose, 1)) * 100}%`,
            }}
          />
        )}
        {stillBooked > 0 && (
          <span
            className="seg ghost"
            style={{
              width: `${(stillBooked / Math.max(bookedByClose, 1)) * 100}%`,
            }}
          />
        )}
      </div>
    </div>
  );
}

function CompareCluster({
  icon,
  label,
  sub,
  items,
  render,
}: {
  icon: string;
  label: string;
  sub: string;
  items: ComparisonLiveItem[];
  render: (item: ComparisonLiveItem) => React.ReactNode;
}) {
  return (
    <div className="kcl">
      <div className="kcl-head">
        <div className="kcl-lab">
          <Icon name={icon} size={14} className="ic" />
          {label}
        </div>
        <span className="kcl-sub">{sub}</span>
      </div>
      <div className="cmpx-rows">
        {items.map(item => (
          <div key={item.filter} className="cmpx-row">
            <span
              className="cmp-chip-dot"
              style={{ background: item.color }}
            />
            <span className="cmpx-name">{item.label}</span>
            <span className="cmpx-vals">{render(item)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function CompareSourceMix({ items }: { items: ComparisonLiveItem[] }) {
  // Union of sources across scopes, in the canonical order, so each row's
  // stacked bar reads the same left-to-right.
  const present = new Set<string>();
  items.forEach(i =>
    i.live?.sourceBreakdown?.sources?.forEach(s => present.add(s.source)),
  );
  const sources = SOURCE_ORDER.filter(s => present.has(s));
  if (sources.length === 0) return null;

  return (
    <section className="srcmix">
      <div className="sec-head">
        <h2>Reservation source mix</h2>
        <span className="sh-meta">
          Today&apos;s guests by reservation origin · per scope
        </span>
        <span
          className="prov-note"
          title="Derived from reservation origin per compared scope."
        >
          From reservations
        </span>
      </div>
      <div className="ol-card">
        {items.map(item => {
          const bd = item.live?.sourceBreakdown;
          const rows = bd?.sources ?? [];
          const ordered = sources
            .map(src => rows.find(s => s.source === src))
            .filter((s): s is NonNullable<typeof s> => !!s);
          return (
            <div key={item.filter} className="cmpx-mix-row">
              <div className="cmpx-scope" style={{ marginBottom: 0 }}>
                <span
                  className="cmp-chip-dot"
                  style={{ background: item.color }}
                />
                <b>{item.label}</b>
              </div>
              <div className="srcmix-bar" style={{ marginBottom: 0 }}>
                {ordered.map(s => (
                  <span
                    key={s.source}
                    className="srcmix-seg"
                    style={{
                      width: `${s.share}%`,
                      background: sourceMeta(s.source).color,
                    }}
                    title={`${sourceMeta(s.source).label} · ${fmt.num(s.covers)} guests (${s.share}%)`}
                  />
                ))}
              </div>
              <div className="cmpx-mix-total">
                {fmt.num(bd?.totalCovers ?? 0)}
                <span>{i18n.t('performance:drillUi.guests')}</span>
              </div>
            </div>
          );
        })}
        <div className="cmpx-legend">
          {sources.map(src => {
            const m = sourceMeta(src);
            return (
              <span key={src} className="kcl-leg">
                <span
                  className="srcmix-dot"
                  style={{ background: m.color }}
                />
                {m.label}
              </span>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function CompareFleetTable({ items }: { items: ComparisonLiveItem[] }) {
  const rows = items
    .flatMap(item =>
      (item.live?.outlets ?? []).map(o => ({ item, o })),
    )
    .sort((a, b) => b.o.coversToday - a.o.coversToday);
  if (rows.length === 0) return null;

  const shown = rows.slice(0, 15);

  return (
    <div className="ol-card optbl-card" style={{ marginBottom: 22 }}>
      <div className="optbl-hint">
        <Icon name="info" size={14} />
        Sorted by guests today · dot marks the compared scope each outlet
        belongs to
      </div>
      <div className="optbl-scroll">
        <div
          className="opt-head"
          style={{ gridTemplateColumns: '2fr 1fr 0.8fr 0.8fr 0.8fr' }}
        >
          <span className="opt-h l">Outlet</span>
          <span className="opt-h">{i18n.t('performance:metrics.covers')}</span>
          <span className="opt-h">Occupancy</span>
          <span className="opt-h">No-show</span>
          <span className="opt-h">Cancel</span>
        </div>
        {shown.map(({ item, o }) => (
          <div
            key={`${item.filter}:${o.id}`}
            className="opt-row"
            style={{ gridTemplateColumns: '2fr 1fr 0.8fr 0.8fr 0.8fr' }}
          >
            <div className="opt-name">
              <span
                className="cmp-chip-dot"
                style={{ background: item.color, marginRight: 9 }}
              />
              <div>
                <b>{o.name}</b>
                <span>{item.label}</span>
              </div>
            </div>
            <div className="opt-cell">
              <span className="opt-v">{fmt.num(o.coversToday)}</span>
            </div>
            <div className="opt-cell">
              <span className="opt-v">
                {Math.round((o.seated / (o.seats || 1)) * 100)}%
              </span>
            </div>
            <div className="opt-cell">
              <span className="opt-v">{o.noShow}%</span>
            </div>
            <div className="opt-cell">
              <span className="opt-v">{o.cancelRate}%</span>
            </div>
          </div>
        ))}
      </div>
      {rows.length > shown.length && (
        <div className="optbl-hint" style={{ borderTop: '1px solid var(--line)' }}>
          +{rows.length - shown.length} more outlets across the compared scopes
        </div>
      )}
    </div>
  );
}
